// One-time, idempotent migration of the legacy hard-coded curricula (AP / SAT /
// ACT) into the unified `curriculum_libraries` model. Runs entirely client-side
// when an admin clicks "Import existing AP/SAT/ACT" on the Hub.
//
// Strategy:
//   - For each legacy source, if a library with the matching `builtInKey` already
//     exists, skip it (safe to re-run).
//   - Otherwise create the library doc (with a stable slug) and copy
//     categories -> textbooks -> resources, PRESERVING original document ids so
//     that resource `parentId` / `folderId` cross-references stay valid.
//   - Storage objects are NOT moved: covers/files are referenced by absolute
//     download URLs which keep working, and `storagePath` is copied verbatim so
//     deletes of legacy files still resolve.
//   - Legacy collections are left intact (nothing is deleted).

import { db } from '../../../firebaseConfig';
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { LIBRARIES_COLLECTION } from './curriculumService';
import { LAYOUT_TYPES } from './curriculumLibrariesService';

// Legacy AP stores its categories at the top level of `ap_curriculum_categories`.
// Legacy SAT/ACT use a single fixed category document inside their collection.
export const LEGACY_SOURCES = [
  {
    builtInKey: 'ap',
    name: 'AP Curriculum',
    description: 'Advanced Placement textbooks grouped by subject area.',
    slug: 'ap',
    layoutType: LAYOUT_TYPES.CATEGORY,
    collectionName: 'ap_curriculum_categories',
  },
  {
    builtInKey: 'sat',
    name: 'SAT Curriculum',
    description: 'SAT preparation textbooks and resources.',
    slug: 'sat',
    layoutType: LAYOUT_TYPES.SIMPLE,
    collectionName: 'new_sat_curriculum_categories',
  },
  {
    builtInKey: 'act',
    name: 'ACT Curriculum',
    description: 'ACT preparation textbooks and resources.',
    slug: 'act',
    layoutType: LAYOUT_TYPES.SIMPLE,
    collectionName: 'act_curriculum_categories',
  },
];

function stripMeta(data = {}) {
  // Keep all content fields; drop server-managed timestamps so we can stamp fresh
  // updatedAt while preserving the original orderIndex/createdAt where present.
  const { ...rest } = data;
  return rest;
}

async function libraryExistsForKey(builtInKey) {
  const q = query(collection(db, LIBRARIES_COLLECTION), where('builtInKey', '==', builtInKey));
  const snap = await getDocs(q);
  return !snap.empty;
}

async function legacyHasContent(source) {
  const snap = await getDocs(collection(db, source.collectionName));
  if (snap.empty) return false;
  // A source counts as having content if any category holds at least one textbook.
  for (const categoryDoc of snap.docs) {
    const textbooks = await getDocs(
      collection(db, source.collectionName, categoryDoc.id, 'textbooks')
    );
    if (!textbooks.empty) return true;
  }
  // Categories with no textbooks still count for AP (so the structure carries over).
  return snap.size > 0;
}

// Returns the list of legacy sources that still need importing (have content and
// no matching library yet). Used by the Hub to decide whether to show the button.
export async function getPendingLegacySources() {
  const pending = [];
  for (const source of LEGACY_SOURCES) {
    // eslint-disable-next-line no-await-in-loop
    if (await libraryExistsForKey(source.builtInKey)) continue;
    // eslint-disable-next-line no-await-in-loop
    if (await legacyHasContent(source)) pending.push(source);
  }
  return pending;
}

async function copyTextbookResources(source, categoryId, textbookId, targetLibraryId) {
  const resourcesSnap = await getDocs(
    collection(db, source.collectionName, categoryId, 'textbooks', textbookId, 'resources')
  );
  for (const resourceDoc of resourcesSnap.docs) {
    const targetRef = doc(
      db,
      LIBRARIES_COLLECTION,
      targetLibraryId,
      'categories',
      categoryId,
      'textbooks',
      textbookId,
      'resources',
      resourceDoc.id
    );
    // eslint-disable-next-line no-await-in-loop
    await setDoc(targetRef, { ...stripMeta(resourceDoc.data()), updatedAt: serverTimestamp() });
  }
}

async function migrateSource(source) {
  if (await libraryExistsForKey(source.builtInKey)) {
    return { builtInKey: source.builtInKey, skipped: true };
  }

  // Create the library doc with a fixed id-less ref but a stable slug.
  const libraryRef = doc(collection(db, LIBRARIES_COLLECTION));
  await setDoc(libraryRef, {
    name: source.name,
    description: source.description,
    layoutType: source.layoutType,
    slug: source.slug,
    builtInKey: source.builtInKey,
    orderIndex: LEGACY_SOURCES.findIndex((s) => s.builtInKey === source.builtInKey),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  const categoriesSnap = await getDocs(collection(db, source.collectionName));
  let categoryCount = 0;
  let textbookCount = 0;

  for (const categoryDoc of categoriesSnap.docs) {
    const targetCategoryRef = doc(
      db,
      LIBRARIES_COLLECTION,
      libraryRef.id,
      'categories',
      categoryDoc.id
    );
    // eslint-disable-next-line no-await-in-loop
    await setDoc(targetCategoryRef, { ...stripMeta(categoryDoc.data()), updatedAt: serverTimestamp() });
    categoryCount += 1;

    // eslint-disable-next-line no-await-in-loop
    const textbooksSnap = await getDocs(
      collection(db, source.collectionName, categoryDoc.id, 'textbooks')
    );
    for (const textbookDoc of textbooksSnap.docs) {
      const targetTextbookRef = doc(
        db,
        LIBRARIES_COLLECTION,
        libraryRef.id,
        'categories',
        categoryDoc.id,
        'textbooks',
        textbookDoc.id
      );
      // eslint-disable-next-line no-await-in-loop
      await setDoc(targetTextbookRef, { ...stripMeta(textbookDoc.data()), updatedAt: serverTimestamp() });
      textbookCount += 1;
      // eslint-disable-next-line no-await-in-loop
      await copyTextbookResources(source, categoryDoc.id, textbookDoc.id, libraryRef.id);
    }
  }

  return { builtInKey: source.builtInKey, skipped: false, categoryCount, textbookCount };
}

export async function migrateLegacyCurriculum() {
  const results = [];
  for (const source of LEGACY_SOURCES) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await migrateSource(source));
  }
  return results;
}
